import {Component, EventEmitter, inject, Input, Output} from '@angular/core';
import { UploadComponentService } from './data-access/upload-component-service';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FileDropDirective } from './directive/file-drop.directive';
import { Status, UploadedFileInformation } from '@shared-types/shared-upload';
import { FileData, FileDataUpload, WorkflowType } from '@shared-types/shared-file';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PaFileMetadata,PaFileMetadataService } from '@pa-angular/pa-file-metadata';
import { FileMetaDataEnvService } from 'projects/dora/src/assets/env/services/file-metadata-env.service'
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent} from './snackbar/snackbar.component';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'shared-upload',
  standalone: true,
  imports: [
    MatIconModule,
    CommonModule,
    MatTooltipModule,
    FileDropDirective,
    MatProgressSpinnerModule,
    TranslateModule
],
  templateUrl: './upload.component.html',
  styleUrl: './upload.component.scss'
})
export class UploadComponent {
  // Used for unique identification in frontend
  @Input() identifier: string | undefined = undefined;
  // Defines the storage location of the running process
  @Input() workflowId!: string;
  @Input() workflowType!: WorkflowType;
  @Input() maxFileSize: number = 104857600;
  @Input() disabled: boolean = false;
  @Input() fileLimit: number = 1;
  @Input() fileFormats: string[] = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword'
  ];
  @Input() fileList: FileData[] | undefined = [];
  @Output() fileProcessEvent: EventEmitter<UploadedFileInformation> =
    new EventEmitter<UploadedFileInformation>();
  @Output() deleteFileEvent: EventEmitter<FileData> =
    new EventEmitter<FileData>();
  @Input() fileExtConfidentialityCheck: string[] = [
    'docx',
    'pdf'
  ];

  uploadService = inject(UploadComponentService);
  fileMetadataService = inject(PaFileMetadataService);
  fileMetaDataEnvService = inject(FileMetaDataEnvService);
  snackBar = inject(MatSnackBar);;

  tempFiles: FileData[] = [];
  parsedMetaData: PaFileMetadata[] = [];

  public processingFailedTooltip: string = `Unable to process the file: Make sure the file is not encrypted or corrupted`
  public supportText: string = this.translateService.instant('UnsupportedFiles')
  public isStatusNotAwaitInput: boolean = false

  constructor(private translateService: TranslateService) {
    this.uploadService.processes
      .pipe(takeUntilDestroyed())
      .subscribe((uploadData: UploadedFileInformation) => {
        this.removeTempFile(uploadData);
        this.fileProcessEvent.emit(uploadData);
      });
  }


  public resetChangeDetection(): void {
    const input = document.getElementById('file');
    // @ts-ignore
    input!['value'] = null;
  }

  async onFileChange(fileList: EventTarget | File[] | FileList | Event | null) {
    const confidentialFiles: FileData[] = [];
    const unclassifiedFiles: FileData[] = [];
    const lengthFileList = this.fileList?.length ? this.fileList?.length : 0;
    if (fileList && 'files' in fileList) {
      // @ts-ignore
      for (const key of Object.keys(fileList['files'])) {
        let status = 'running' as Status;
        if (
          // @ts-ignore
        this.fileFormats.includes(fileList['files'][key].type) &&
          // @ts-ignore
              fileList['files'][key].size <= this.maxFileSize &&
              lengthFileList + this.tempFiles.length < this.fileLimit
        ) {
          /**
           * Check if a file is classified (confidentiallity)
           */
          // @ts-ignore
          const isFileClassified = await this.isFileClassified(fileList['files'][key]);
          if (!this.fileMetaDataEnvService.allowUnclassifiedFilesUpload && !isFileClassified) {
            // @ts-ignore
            unclassifiedFiles.push(fileList['files'][key]);
            continue;
          }

          // @ts-ignore
          const isFileConfidential = await this.isFileConfidential(fileList['files'][key]);
          if (isFileConfidential) {
            // @ts-ignore
            confidentialFiles.push(fileList['files'][key]);
            continue;
          }

          this.uploadService.uploadFile$.next(
            // @ts-ignore
            this.createFileConfig(fileList['files'][key])
          );
          // @ts-ignore
          this.addTempFile(fileList['files'][key], status);
        } else {
          status = 'failed' as Status;
          // @ts-ignore
          if (fileList['files'][key].size > this.maxFileSize) {
            this.fileSizeTooBig();
          }
        }
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    } else if (fileList) {
      for (const key of Object.keys(fileList)) {
        let status = 'running' as Status;
        if (
          // @ts-ignore
          this.fileFormats.includes(fileList[key].type) &&
          // @ts-ignore
          fileList[key].size <= this.maxFileSize &&
          lengthFileList + this.tempFiles.length < this.fileLimit
        ) {
          /**
           * Check if a file is classified (confidentiallity)
           */
           // @ts-ignore
          const isFileClassified = await this.isFileClassified(fileList[key]);
          if (!this.fileMetaDataEnvService.allowUnclassifiedFilesUpload && !isFileClassified) {
             // @ts-ignore
            unclassifiedFiles.push(fileList[key]);
            continue;
          }
           // @ts-ignore
          const isFileConfidential = await this.isFileConfidential(fileList[key]);
          if (isFileConfidential) {
             // @ts-ignore
            confidentialFiles.push(fileList[key]);
            continue;
          }

          this.uploadService.uploadFile$.next(
            // @ts-ignore
            this.createFileConfig(fileList[key])
          );
          // @ts-ignore
          this.addTempFile(fileList[key], status);
        } else {
          status = 'failed' as Status;
          // @ts-ignore
          if (fileList[key].size > this.maxFileSize) {
            this.fileSizeTooBig();
          }
        }
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    }

	let title: string = '';
    const messages: string[] = [];
    if (confidentialFiles.length) {
      title = 'Confidential files';
      messages.push(
       'Some files were detected to be confidential and therefore excluded from the upload!'
      );
    }
	if (unclassifiedFiles.length) {
		title += title
		  ? '. '
		  : ''; // Append a period if title already exists
		title += 'Upload blocked due to missing classification of the document';

		messages.push(`
		  The upload is blocked by your organisation's security policy.
		  <br/><br/>
		  Important note: In many cases, you can right-click on the document > select the "Classify and protect" option > In the new window, you can view and change the selected confidentiality level and then use the document.
		  <br/><br/>
		  Complete information and the instructions for
		  <a href="https://helanet.hlb.helaba.de/sites/de/about-us/departments/043000/Documents/Awareness/OnePager/Erkennen%20der%20Vertraulichkeit%20von%20Dokumenten_1.0.pdf"
			target="_blank"
			rel="noopener">
			Klassifizierung von Informationen
		  </a>
		  und zur
		  <a href="https://helanet.hlb.helaba.de/sites/de/about-us/departments/043000/Documents/Awareness/OnePager/Erkennen%20der%20Vertraulichkeit%20von%20Dokumenten_1.0.pdf"
			target="_blank"
			rel="noopener">
			Vertraulichkeit von Dokumenten
		  </a>.
		`);
    }

    if (messages.length) {
		this.snackBar.openFromComponent(SnackbarComponent, {
			duration: 3000,
			data: {title,message: messages.join('<br/>')},
			horizontalPosition: 'center',
    		verticalPosition: 'top'
		  });
  	}
}

  public invalidFiles(file: File): void {
  }

  private fileSizeTooBig(): void {
  }

  private createFileConfig(file: File) {
    const fileData = {
      file: file,
      identifier: this.identifier,
      originalFilename: file.name
    } as FileDataUpload;

    if (this.workflowId && this.workflowType) {
      fileData.workflowId = this.workflowId;
      fileData.workflowType = this.workflowType;
    }
    return fileData;
  }

  private addTempFile(file: File, status: Status) {
    this.tempFiles.push({
      frontendIdentifier: this.identifier,
      displayName: file.name,
      fileExtension: file.name.split('.').pop()!,
      status: status,
      storedName: undefined,
      extractedDataPath: {
        embeddedFilenames: undefined,
        textDataFilename: undefined,
        imageDataFilename: undefined
      }
    });
  }

  private removeTempFile(uploadData: UploadedFileInformation) {
    this.tempFiles = this.tempFiles.filter(
      (element: FileData) => !(element.displayName == uploadData.displayName)
    );
  }

  public deleteFile(file: FileData): void {
        this.deleteFileEvent.emit(file);
        this.uploadService.deleteFile$.next({
          file: file,
          workflowId: this.workflowId,
          workflowType: this.workflowType
        });
  }

  

  private async isFileClassified(file: File): Promise<boolean> {
    return new Promise(async (resolve) => {
      const fileExt: string | undefined = file.name.split('.').pop();

      if (!fileExt || !this.fileExtConfidentialityCheck.includes(fileExt)) {
        return resolve(false);
      }
      for (const md of this.fileMetaDataEnvService.existingFilesMetadata) {
        const hasMetadataKey = await this.fileMetadataService.hasMetaDataKey(
            file,
            file.name.split('.').pop(),
            md.propName
          );
        if (hasMetadataKey) {
          return resolve(true);
        }
      }
      return resolve(false);
    });
  }

  private async isFileConfidential(file: File): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      const fileExt: string | undefined = file.name.split('.').pop();
      if (!fileExt || !this.fileExtConfidentialityCheck.includes(fileExt)) {
        return resolve(false);
      }

      if (this.fileMetaDataEnvService.confidentialFilesMetadata.length === 0) {
        return resolve(false);
      } else {
        try {
          for (const md of this.fileMetaDataEnvService.confidentialFilesMetadata) {
            const hasMetadata =
              await this.fileMetadataService.hasMetaDataKeyValue(
                file,
                fileExt,
                md.propName,
                md.propValue
              );
            if (hasMetadata) {
              resolve(true);
            }
          }
        } catch (e) {
          return reject(e);
        }
        return resolve(false);
      }
    });
  }
}
